import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { ApiError } from "../types/api-error";
import {
  logout,
  readAccessToken,
  readRefreshToken,
  writeAccessToken,
  writeRefreshToken,
} from "../lib/auth";
import { showAlert } from "../lib";
import { config } from "../config/env";

console.log("API Client baseURL:", config.apiHost);

const apiClient = axios.create({
  baseURL: config.apiHost,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(
  async (config) => {
    const accessToken = readAccessToken();
    const id =
      JSON.parse(localStorage.getItem("ggp-zustand-persist") || "{}")?.state
        ?.user?.id ?? null;

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
      config.headers.id = id;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const jwtToken = readAccessToken();
      const refreshToken = readRefreshToken();
      const id =
        JSON.parse(localStorage.getItem("ggp-zustand-persist") || "{}")?.state
          ?.user?.id ?? null;

      if (!refreshToken) {
        await showAlert("로그인해주세요");
        return logout();
      }
      try {
        const response = await axios.post(
          `${config.apiHost}/refresh-token`,
          {
            jwtToken,
            refreshToken,
          },
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              id: id,
            },
          }
        );

        const accessTokenData = response?.data?.accessToken;
        const refreshTokenData = response?.data?.refreshToken;
        writeAccessToken(accessTokenData);
        writeRefreshToken(refreshTokenData);

        return apiClient(originalRequest);
      } catch (e) {
        await showAlert("토큰이 만료되었습니다. 다시 로그인해주세요");
        return logout();
      }
    }

    return Promise.reject(error);
  }
);

/**
 * @param config - axios request config
 * @param isShowError - 에러 발생시 alert를 띄울지 여부
 * @returns
 */
export const request = async <T>(
  config: AxiosRequestConfig,
  isShowError: boolean = true
): Promise<T> => {
  const { method } = config;
  const isGetRequest = method?.toUpperCase() === "GET";

  try {
    const { data } = await apiClient.request<T>({ ...config });

    if (!isGetRequest && (data as any)?.success === false) {
      throw new ApiError({
        message: (data as any)?.message ?? "서버 요청 에러",
      });
    }

    return data;
  } catch (error) {
    let message = "서버 요청 에러";

    if (error instanceof AxiosError) {
      const { response }: any = error;
      if (response?.data?.message) {
        message = response.data.message;
      }

      if (isGetRequest) {
        if (error?.response?.status === 403) {
          showAlert("조회 권한이 없습니다");
        }
        return [] as any;
      }
    } else if (error instanceof ApiError) {
      message = error.message;
    }

    if (isShowError && !isGetRequest) {
      showAlert(message);
    }

    throw error;
  }
};

export const setUserIp = (ip: string) => {
  apiClient.defaults.headers.common["ip"] = ip;
};

export { apiClient };
